import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Sale } from '@/api/saleApi';

interface CancelSaleDialogProps {
  sale: Sale | null;
  isSubmitting: boolean;
  error: string;
  onClose: () => void;
  onConfirm: () => void;
}

const CancelSaleDialog: React.FC<CancelSaleDialogProps> = ({
  sale,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!sale) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isSubmitting, onClose, sale]);

  if (!sale) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isSubmitting) onClose();
    }}>
      <section className="w-full max-w-md rounded-[20px] border border-outline-variant bg-white p-5 shadow-xl" role="alertdialog" aria-modal="true" aria-labelledby="cancel-sale-title">
        <div className="flex items-start gap-3">
          <span className="material-symbols-outlined flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-error-container text-error">cancel</span>
          <div className="min-w-0">
            <h2 id="cancel-sale-title" className="font-h2 text-h2 text-on-surface">{t('admin.sales_history.cancel_dialog.title')}</h2>
            <p className="mt-2 text-body-md text-secondary">{t('admin.sales_history.cancel_dialog.description', { code: sale.code })}</p>
            <p className="mt-2 text-body-sm text-error">{t('admin.sales_history.cancel_dialog.inventory_notice')}</p>
          </div>
        </div>
        {error && <div className="mt-4 rounded-xl border border-error/30 bg-error-container/40 px-3 py-2 text-body-sm text-error" role="alert">{error}</div>}
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className="min-h-10 rounded-xl border border-outline-variant px-4 font-label-md text-secondary disabled:opacity-50" disabled={isSubmitting} onClick={onClose}>{t('admin.sales_history.actions.no')}</button>
          <button type="button" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-error px-4 font-label-md text-on-error disabled:opacity-60" disabled={isSubmitting} onClick={onConfirm}>
            {isSubmitting && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
            {t(isSubmitting ? 'admin.sales_history.actions.canceling' : 'admin.sales_history.actions.cancel_sale')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default CancelSaleDialog;
